import { differenceInHours, differenceInMinutes } from "date-fns";

import { Event, EventStatus, ActivityMode, VodType } from "../types";

const env = process.env.REACT_APP_ENV || "dev";

const EventStatusOrder = [
  EventStatus.Preparing,
  EventStatus.BeforeStart,
  EventStatus.Processing,
  EventStatus.ServerOpen,
  EventStatus.Live,
  EventStatus.Encoding,
  EventStatus.ReadyForVOD,
  EventStatus.Vod,
  EventStatus.Ended,
  EventStatus.Archiving,
  EventStatus.Archived,
  EventStatus.Canceling,
  EventStatus.Canceled,
];

const isValidEventStatus = (eventStatus: EventStatus) => EventStatusOrder.includes(eventStatus);

export const isEventBeforeStatus = (event: Event, eventStatus: EventStatus): boolean =>
  isValidEventStatus(event.status) &&
  isValidEventStatus(eventStatus) &&
  EventStatusOrder.indexOf(event.status) < EventStatusOrder.indexOf(eventStatus);

export const isEventAfterOrEqualsToStatus = (event: Event, eventStatus: EventStatus): boolean =>
  isValidEventStatus(event.status) &&
  isValidEventStatus(eventStatus) &&
  EventStatusOrder.indexOf(event.status) >= EventStatusOrder.indexOf(eventStatus);

export const isEventPreviewAvailable = (event: Event): boolean =>
  event.status === EventStatus.BeforeStart && differenceInMinutes(event.schedule_started_at * 1000, Date.now()) < 720;

export const isEventBeforePreviewAvailable = (event: Event): boolean =>
  event.status === EventStatus.Preparing ||
  (event.status === EventStatus.BeforeStart &&
    differenceInMinutes(event.schedule_started_at * 1000, Date.now()) >= 720);

export const isEventAfterOrEqualsToPreviewAvailable = (event: Event): boolean =>
  isValidEventStatus(event.status) && !isEventBeforePreviewAvailable(event);

// Note: "Encoding" in playback mode or vod_replacement is "VOD_processing"
export const isEventInVodProcessing = (event: Event): boolean =>
  event.status === EventStatus.Encoding &&
  (event.activity_mode === ActivityMode.PlaybackMode || event.vod_type === VodType.VodReplacement);

export const isEventStartVodInFiveMinutesOrAfterVod = (event: Event): boolean =>
  (event.status === EventStatus.ReadyForVOD && differenceInMinutes(event.vod_started_at * 1000, Date.now()) < 5) ||
  isEventAfterOrEqualsToStatus(event, EventStatus.Vod);

export const isNeedPollingEventStatus = (event: Event): boolean => {
  if (!isValidEventStatus(event.status)) return false;
  switch (event.status) {
    case EventStatus.Processing:
    case EventStatus.ServerOpen:
    case EventStatus.Live:
      return true;
    default:
      return false;
  }
};

export const isNeedEndedEventStatus = (event: Event): boolean => {
  if (!isValidEventStatus(event.status)) return false;
  switch (event.status) {
    case EventStatus.Canceling:
    case EventStatus.Canceled:
    case EventStatus.Encoding:
    case EventStatus.Ended:
      return true;
    default:
      return false;
  }
};

/**
 * live-to-file clip available remaining hour
 * See #ent_pride_collab: https://kkbox.slack.com/archives/C097SFFD6HJ/p1768185680897889?thread_ts=1767607718.852909&cid=C097SFFD6HJ
 */
export const CLIP_REMAINING_HOURS = env === "dev" ? 12 : 24;

export const getLiveToFileAvailable = (endedAt: Event["ended_at"]): boolean => {
  const remainingHour = endedAt ? differenceInHours(new Date(), new Date(endedAt * 1000)) : null;
  return remainingHour !== null && remainingHour < CLIP_REMAINING_HOURS;
};

export const getLiveToFileRemainingHour = (endedAt: Event["ended_at"]): number | null => {
  const remainingHour = endedAt ? differenceInHours(new Date(), new Date(endedAt * 1000)) : null;
  return remainingHour !== null ? CLIP_REMAINING_HOURS - (remainingHour ?? 0) : null;
};
